import { getCoverageLinesConfig } from '@common/config/coverage-line-config';
import { isArray } from 'lodash';
import { ApprovalStatus, CoverageLine, DuplicationResult } from 'enums';
import { messages } from 'i18n';
import { AnyObject } from 'types';
import { getTimezoneLessDisplayDate } from 'utils/date-utils';

const labels = messages.submissionActivityLog.carrierSubmissionFieldLabels;
const { clearance } = messages.carrierSubmissionWorkspace;

const duplicationStatusLabels: Record<DuplicationResult, string> = {
  [DuplicationResult.Cleared]: clearance.statusCleared,
  [DuplicationResult.Duplicate]: clearance.statusDuplicate,
  [DuplicationResult.Error]: clearance.statusError,
};

export function parseFieldValue(value: string | undefined): unknown {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

const fieldDisplayNormalizers: Partial<Record<string, (rawValue: unknown) => unknown>> = {
  dueDate: (rawValue) => {
    if (typeof rawValue !== 'string') {
      return rawValue;
    }
    return getTimezoneLessDisplayDate(rawValue);
  },
  coverageLines: (rawValue) => {
    if (!isArray(rawValue)) {
      return rawValue;
    }
    const config = getCoverageLinesConfig();
    return (rawValue as string[]).map((enumVal) => config[enumVal as CoverageLine]?.text ?? enumVal);
  },
  industry: (rawValue) => {
    if (typeof rawValue !== 'object' || rawValue === null) {
      return rawValue;
    }
    const { naicsCode, description } = rawValue as AnyObject;
    return { [labels.naicsCode]: naicsCode, [labels.description]: description };
  },
  insuredMailingAddress: (rawValue) => {
    if (typeof rawValue !== 'object' || rawValue === null) {
      return rawValue;
    }
    const { line1, line2, city, state, postalCode } = rawValue as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    if (line1 != null) {
      result[labels.line1] = line1;
    }
    if (line2 != null) {
      result[labels.line2] = line2;
    }
    if (city != null) {
      result[labels.city] = city;
    }
    if (state != null) {
      result[labels.state] = state;
    }
    if (postalCode != null) {
      result[labels.postalCode] = postalCode;
    }
    return result;
  },
  approvalStatus: (rawValue) => {
    if (typeof rawValue !== 'object' || rawValue === null) {
      return rawValue;
    }
    const { status, reason } = rawValue as { status?: string; reason?: string };
    const result: Record<string, string> = {};
    if (status) {
      result[labels.status] = status;
    }
    if (status !== ApprovalStatus.Approved) {
      result[labels.reason] = reason ?? '';
    }
    return result;
  },
  clearanceDetails: (rawValue) => {
    if (typeof rawValue !== 'object' || rawValue === null) {
      return rawValue;
    }
    const { duplication } = rawValue as {
      duplication?: {
        status: DuplicationResult;
        errorReason?: string;
        matchedSubmissionInfo?: {
          matchedSubmissionId: string;
          matchReason: string;
          matchedInsuredName?: string;
          matchingNamedInsured?: string;
        };
      };
    };
    if (!duplication) {
      return rawValue;
    }
    const statusLabel = duplicationStatusLabels[duplication.status] ?? duplication.status;
    const result: Record<string, string> = { [labels.status]: statusLabel };
    if (duplication.errorReason && duplication.status === DuplicationResult.Error) {
      result[labels.errorReason] = duplication.errorReason;
    }
    if (duplication.matchedSubmissionInfo && duplication.status === DuplicationResult.Duplicate) {
      const info = duplication.matchedSubmissionInfo;
      if (info.matchedInsuredName) {
        result[labels.matchedWith] = info.matchedInsuredName;
      }
      result[labels.reason] = info.matchReason;
    }
    return result;
  },
};

export function normalizeFieldValueForDisplay(field: string, rawValue: unknown): unknown {
  return fieldDisplayNormalizers[field]?.(rawValue) ?? rawValue;
}
